import React, { useContext, useEffect, useRef, useState } from 'react';
import { Col, NavLink } from 'reactstrap';
import { NavLink as RRNavLink } from 'react-router-dom';
import AchievementsSummaryContext from '../shared/AchievementsSummaryContext.tsx';
import classes from './Badges.module.scss';

// Die Grafiken sind Produkt-Assets (Ribbon, Icon, Outline pro Errungenschaft) und
// werden als URLs hereingereicht: `assets[type] = { ribbon, icon, outline }`.
// `ribbon` ist optional (Facebook/E-Mail teilen sich kein Ribbon), `icon` und
// `outline` sind Pflicht — fehlen sie, rendert die Komponente nichts.
const BADGE_SETS = {
    isReferral: {
        split: true,
        reversed: true,
        badgeCSS: {
            Icon: { top: '14px' },
            Ribbon: {},
        },
    },
    hasReferred: {
        split: true,
        reversed: true,
        badgeCSS: {
            Icon: { top: '14px' },
            Ribbon: {},
        },
    },
    whatsAppShares: {
        split: false,
        reversed: false,
        badgeCSS: {
            Icon: {},
            Ribbon: { top: '20px' },
        },
    },
    facebookShares: {
        split: false,
        reversed: false,
        badgeCSS: {
            Icon: {},
            Ribbon: { top: '20px' },
        },
    },
    emailShares: {
        split: false,
        reversed: false,
        badgeCSS: {
            Icon: {},
            Ribbon: { top: '20px' },
        },
    },
};

// Das Ziel der Abzeichen ist Produktdaten: die Errungenschaften-Seite existiert nur in der
// Förder-Produktlinie. Ohne `linkTo` rendert das Abzeichen ohne Link.
const Badge = ({ type, isAchieved, isLink = true, linkTo = null, assets = {} }) => {
    const achievements = useContext(AchievementsSummaryContext);
    const badgeContainerRef = useRef(null);
    const badgeRef = useRef(null);
    const [isAnimating, setIsAnimating] = useState(false);

    useEffect(() => {
        if (!badgeContainerRef.current) return;

        const handleScroll = () => {
            const scrollY = window.scrollY || window.pageYOffset;
            const triggerThreshold = 20;

            if (Math.abs(scrollY) > triggerThreshold && !isAnimating) {
                setIsAnimating(true);

                if (badgeContainerRef.current) {
                    badgeContainerRef.current.classList.add(classes.AnimatedRibbon);
                    setTimeout(() => {
                        if (badgeContainerRef.current) {
                            badgeContainerRef.current.classList.remove(classes.AnimatedRibbon);
                        }
                        setIsAnimating(false);
                    }, 2400);
                }
            }
        };

        const handleOrientationChange = (event) => {
            const { gamma } = event;

            if (badgeContainerRef.current && badgeRef.current) {
                const rotationAngle = gamma * -1;
                const viewportWidth = window.innerWidth;
                const badgeRect = badgeContainerRef.current.getBoundingClientRect();
                const badgePositionRatio = badgeRect.x / viewportWidth;

                const maxRotationRange = 35;
                const minRotationRange = 10;
                const rotationRange = minRotationRange + (maxRotationRange - minRotationRange) * badgePositionRatio;

                const cappedRotationAngle = Math.max(-rotationRange, Math.min(rotationRange, rotationAngle));

                badgeContainerRef.current.style.transform = `rotate(${cappedRotationAngle}deg)`;

                const counterRotationAngle = -cappedRotationAngle;
                badgeRef.current.style.transform = `rotate(${counterRotationAngle}deg)`;
            }
        };

        window.addEventListener('scroll', handleScroll);
        window.addEventListener('deviceorientation', handleOrientationChange);

        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('deviceorientation', handleOrientationChange);
        };
    }, [isAnimating]);

    const badgeSet = BADGE_SETS[type];
    const { ribbon: badgeRibbon, icon: badgeIcon, outline: badgeOutline } = assets[type] || {};

    if (!badgeSet || !badgeIcon || !badgeOutline) {
        return null;
    }

    const { reversed, badgeCSS } = badgeSet;
    const achievementData = achievements?.[type];

    const ariaLabel = achievementData
        ? `Errungenschaft: ${achievementData.name}. Status: ${isAchieved ? 'Erreicht' : 'Nicht erreicht'}.`
        : `Link zur Errungenschaft ${type}`;

    const badgeContent = (
        <>
            <div ref={badgeContainerRef} className={`position-relative ${classes.BadgeContainer} ${classes.Z1}`}>
                {isAchieved &&
                    (reversed ? (
                        <>
                            <img
                                className={`position-absolute mx-auto start-0 end-0 ${
                                    isAnimating ? classes.AnimatedRibbon : ''
                                } ${classes.Z1}`}
                                style={badgeCSS.Ribbon}
                                src={badgeRibbon}
                                alt=""
                            />
                            <img
                                ref={badgeRef}
                                className={`position-absolute mx-auto start-0 end-0 ${
                                    isAnimating ? classes.AnimatedIcon : ''
                                } ${classes.Z2}`}
                                style={badgeCSS.Icon}
                                src={badgeIcon}
                                alt=""
                            />
                        </>
                    ) : (
                        <>
                            <img
                                ref={badgeRef}
                                className={`position-absolute mx-auto start-0 end-0 ${
                                    isAnimating ? classes.AnimatedIcon : ''
                                } ${classes.Z2}`}
                                style={badgeCSS.Icon}
                                src={badgeIcon}
                                alt=""
                            />
                            {badgeRibbon && (
                                <img
                                    className={`position-absolute mx-auto start-0 end-0 ${
                                        isAnimating ? classes.AnimatedRibbon : ''
                                    } ${classes.Z1}`}
                                    style={badgeCSS.Ribbon}
                                    src={badgeRibbon}
                                    alt=""
                                />
                            )}
                        </>
                    ))}
            </div>
            <img
                className={`position-absolute mx-auto start-0 end-0 ${classes.BadgeOutline} ${classes.Z0}`}
                src={badgeOutline}
                alt=""
            />
        </>
    );

    return (
        <Col xs={2} className="p-0">
            {isLink && linkTo ? (
                <NavLink
                    className={`position-relative anl__badge--${type} ${classes.Z2}`}
                    to={linkTo}
                    tag={RRNavLink}
                    exact
                    aria-label={ariaLabel}
                >
                    {badgeContent}
                </NavLink>
            ) : (
                <div
                    className={`position-relative anl__badge--${type} ${classes.Z2} d-block`}
                    aria-label={ariaLabel}
                    role="img"
                >
                    {badgeContent}
                </div>
            )}
        </Col>
    );
};

export default Badge;
